import React, { useEffect, useState } from 'react';
import { Loader2, Send, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const STATUS_STYLE = {
  accepted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
  duplicate: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');

function Verdict({ ok, label, testid }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div data-testid={testid} className={`flex items-center gap-2 text-xs ${ok ? 'text-emerald-300' : 'text-red-300'}`}>
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </div>
  );
}

export function RazorpaySignatureTest({ configured }) {
  const [candidate, setCandidate] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await adminApi.post('/admin/payment-integrations/razorpay/test-signature', {
        candidate_secret: candidate.trim() || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  if (!configured) return null;
  return (
    <div data-testid="razorpay-signature-test" className="rounded-lg bg-slate-800/40 border border-slate-700 p-4 space-y-3">
      <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider">Signature check</div>
      <p className="text-[11px] text-slate-500">
        Optional: paste the secret from Razorpay → Webhooks to compare it with the saved one. Nothing is stored.
      </p>
      <div className="flex gap-2">
        <Input
          type="password"
          value={candidate}
          onChange={(e) => setCandidate(e.target.value)}
          placeholder="Secret from Razorpay dashboard (optional)"
          data-testid="razorpay-test-candidate-secret"
          className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
        />
        <Button
          type="button"
          onClick={run}
          disabled={running}
          data-testid="razorpay-test-signature-btn"
          className="bg-blue-600 hover:bg-blue-500 text-white shrink-0"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send test signature
        </Button>
      </div>
      {result && (
        <div className="space-y-1.5" data-testid="razorpay-test-result">
          {result.candidate_matches !== null && (
            <Verdict
              testid="razorpay-test-candidate-verdict"
              ok={result.candidate_matches}
              label={result.candidate_matches
                ? 'Pasted secret matches the saved secret.'
                : `Pasted secret does NOT match the saved secret (…${result.stored_secret_last4}). Re-save the Razorpay secret here.`}
            />
          )}
          <Verdict
            testid="razorpay-test-selftest-verdict"
            ok={result.self_test?.accepted}
            label={result.self_test?.accepted
              ? 'Receiver verified a signed test event (HTTP 200).'
              : `Receiver rejected the test event (${result.self_test?.status_code ?? result.self_test?.error ?? 'no response'}).`}
          />
        </div>
      )}
    </div>
  );
}

export function RazorpayDeliveries() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/payment-integrations/razorpay/deliveries?limit=50');
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {
      toast.error('Could not load Razorpay deliveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section data-testid="razorpay-deliveries" className="mt-6 bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-white font-semibold">Razorpay webhook deliveries</div>
          <div className="text-xs text-slate-400">{total} events received at /api/webhooks/razorpay</div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={load} data-testid="razorpay-deliveries-refresh"
          className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-slate-500 text-sm py-8" data-testid="razorpay-deliveries-empty">
          {loading ? 'Loading…' : 'No Razorpay deliveries yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="razorpay-deliveries-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Payment / Order</th><th className="py-2">Reason / License</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b border-slate-800/60" data-testid={`razorpay-delivery-row-${d.id}`}>
                  <td className="py-2 pr-3 text-slate-400 text-xs whitespace-nowrap">{fmt(d.received_at)}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[d.status] || 'text-slate-300 border-slate-600'}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-slate-300">
                    {d.event_id || '—'}<div className="text-slate-500">{d.order_id || ''}</div>
                  </td>
                  <td className="py-2 font-mono text-xs text-slate-300">{d.reason || (d.license_id ? `license ${d.license_id}` : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
